import { useCallback } from 'react'
import { coerceAssistantMessageContent } from '../coerceAssistantMessageContent'
import type { AgentAnswerFormat } from '../formatAgentXmlToMarkdown'
import { formatAgentXmlToMarkdown } from '../formatAgentXmlToMarkdown'
import type { ContextUsage } from '../omicsContextUsage'
import type { FileChange } from '../filesChanged'
import type { FileTag } from '@/shared/utils/crud-helper/models'

export type { ContextUsage } from '../omicsContextUsage'

/**
 * A single intermediate step captured by the agent (generate or execute node).
 * Debug-specific fields are only populated when debug_mode.
 */
export interface AgentStep {
  stepIndex: number
  node: 'generate' | 'execute' | 'relevance_check'
  /** Raw content output of this step (with all XML tags intact). */
  content?: string
  /** Narrative bucket when streaming structured segments (AG-UI v1). */
  segmentKind?: 'preamble' | 'reasoning' | 'final'
  /** Debug only: prompt sent to the LLM for this step. */
  promptSent?: string
  /** Debug only: raw model output. */
  assistantContent?: string
  /** Debug only: observation text. */
  observation?: string
  /** Debug only: execution metadata (executor type, code length, etc.) */
  debug?: { executor: string; code_length: number; result_length: number; has_error: boolean }
}

/** AG-UI persistence / replay timeline (matches omicsai bioagent stream.py). */
export type TimelineSegmentKind = 'preamble' | 'reasoning' | 'final'

export interface TimelineTextEntry {
  kind: 'text'
  segment_kind: TimelineSegmentKind
  content: string
  step_index: number
}

export interface TimelineToolCallEntry {
  kind: 'tool_call'
  tool_call_id: string
  tool_name: string
  tool_input: Record<string, unknown>
  display_summary?: string
  skill_name?: string
  step_index: number
}

export interface TimelineToolResultEntry {
  kind: 'tool_result'
  tool_call_id: string
  tool_name: string
  content: string
  ok: boolean
  error?: string | null
  step_index: number
}

export type TimelineEntry = TimelineTextEntry | TimelineToolCallEntry | TimelineToolResultEntry

export interface AssistantTimelineV2 {
  schema_version: 2
  entries: TimelineEntry[]
}

export type StreamAlert = { kind: 'cancel' | 'error' | 'info'; message: string }

/** Live-turn Working strip item (assistant-ui / sidebar streaming layout). */
export type LiveWorkingQueueItem =
  | { kind: 'reasoning'; markdown: string }
  | { kind: 'tool'; toolCallId: string }

/** Token usage data for a single response (forwarded from the agent). */
export interface TokenUsage {
  input_tokens: number
  output_tokens: number
  total_tokens: number
  llm_calls: number
}

// Message type definition
export interface Message {
  id: string
  type: 'bot' | 'user' | 'agent-response'
  content: string
  /** User rows: the composer's file/folder chips sent with this prompt, shown in its bubble. */
  attachedFiles?: readonly FileTag[]
  /** Intermediate agent steps (debug / legacy); prefer `timeline` for AG-UI v1. */
  steps?: AgentStep[]
  /** Tools used for this response (e.g. search_internet) - shown as label in UI */
  toolsUsed?: string[]
  /** Suggested follow-up prompts for this turn - rendered as clickable chips under the message. */
  suggestedFollowups?: string[]
  /** KB document sources when query_omicsbox_kb was used - for citations */
  kbSources?: Array<{ source: string; relevance?: number }>
  /** Web sources when search_internet was used - for citations with links */
  internetSources?: Array<{ title?: string; url: string }>
  /** Token usage for this response (debug mode only). */
  tokenUsage?: TokenUsage
  /** Last-known context window fill after this assistant turn. */
  contextUsage?: ContextUsage
  /** True when the agent compacted history during this turn. */
  contextCompacted?: boolean
  /** Model used for this response (e.g. "bedrock/anthropic.claude-sonnet-4-5-..."). */
  modelUsed?: string
  /** Server-computed cloud unit estimate (units + model label). */
  cloudUnits?: { units: number; modelLabel: string }
  /** AWS AgentCore label from control plane (e.g. omicsai_bioagent_v3-24). */
  agentRuntimeLabel?: string
  /** When true, this message is the recursion-limit notice; show retry/continue actions. */
  recursionLimitHit?: boolean
  /** When recursionLimitHit, the user message content to resend when user clicks Retry. */
  recursionLimitUserMessage?: string
  /** Structured turn timeline (schema v2); AG-UI v1 / Deep Agents. */
  timeline?: AssistantTimelineV2
  /** Server signals prose was normalized (``RUN_FINISHED.answer_format``). */
  answerFormat?: AgentAnswerFormat
  /** Inline alert (cancel mid-run, tool failure). */
  streamAlert?: StreamAlert
  /** Files this turn created, changed, moved or deleted (`files_changed`), shown in the run footer. */
  filesChanged?: FileChange[]
  /** Full number of changes when `files_changed` was shortened (`files_changed_total`). */
  filesChangedTotal?: number
  /** `files_changed` is shorter than what the turn changed (`files_changed_truncated`). */
  filesChangedTruncated?: true
  /** True while the sidebar is showing an in-flight bot row with layered lanes. */
  isStreamingTurn?: boolean
  /** Text committed before the first tool (plain lane). */
  liveLeadMarkdown?: string
  /** Reasoning chunks and tools in order for the Working strip. */
  liveWorkingQueue?: LiveWorkingQueueItem[]
}

// Unique ID generator to prevent duplicate keys
let messageCounter = 0
export const generateUniqueId = () => {
  messageCounter += 1
  return `${Date.now()}-${messageCounter}-${Math.random().toString(36).substr(2, 9)}`
}

export const useChatMessageFormatting = () => {
  // Function to process streaming content for real-time display (tag cleanup → markdown)
  const processStreamingContent = useCallback((content: string): string => {
    const normalized = coerceAssistantMessageContent(content)
    const afterThink = normalized
      .replace(/<think>[\s\S]*?<\/redacted_thinking>/g, '\n\n*Thinking…*\n\n')
      .replace(/<think>[\s\S]*$/g, '\n\n*Thinking…*\n\n')
    return formatAgentXmlToMarkdown(afterThink)
  }, [])

  // Function to create welcome message
  const createWelcomeMessage = useCallback((userName?: string): Message => {
    const greeting = userName
      ? `Hi ${userName}! I'm your OmicsBox AI Assistant.`
      : `Hi there! I'm your OmicsBox AI Assistant.`

    return {
      id: generateUniqueId(),
      type: 'bot' as const,
      content: `${greeting}

I can help with:

🔬 Bioinformatics & analysis workflows  
📊 Data interpretation & visualization  
💡 Tool tips, parameters & best practices  
🛠️ File issues & command-line support  

Just ask — I'm here to make your research easier!`,
    }
  }, [])

  // Function to create user message
  const createUserMessage = useCallback((content: string): Message => ({
    id: generateUniqueId(),
    type: 'user' as const,
    content,
  }), [])

  // Function to create bot message
  const createBotMessage = useCallback((content: string, id?: string, timeline?: AssistantTimelineV2): Message => {
    const base: Message = {
      id: id || generateUniqueId(),
      type: 'bot' as const,
      content,
      ...(timeline && { timeline }),
    }
    if (timeline?.schema_version === 2) {
      return {
        ...base,
        isStreamingTurn: true,
        liveLeadMarkdown: '',
        liveWorkingQueue: [],
      }
    }
    return base
  }, [])

  /** Create an agent-response message: final markdown + optional v2 timeline and metadata. */
  const createAgentResponseFromSteps = useCallback(
    (
      id: string,
      finalContent: string,
      steps: AgentStep[],
      toolsUsed?: string[],
      kbSources?: Array<{ source: string; relevance?: number }>,
      internetSources?: Array<{ title?: string; url: string }>,
      timeline?: AssistantTimelineV2,
      answerFormat?: AgentAnswerFormat,
    ): Message => ({
      id,
      type: 'agent-response' as const,
      content: finalContent,
      ...(steps.length > 0 && { steps }),
      toolsUsed,
      kbSources,
      internetSources,
      ...(timeline && { timeline }),
      ...(answerFormat && { answerFormat }),
    }),
    [],
  )

  return {
    processStreamingContent,
    createWelcomeMessage,
    createUserMessage,
    createBotMessage,
    createAgentResponseFromSteps,
    generateUniqueId,
  }
}
